import { getT } from '@/i18n';
import { create } from 'zustand';
import { api } from '@/lib/api';
import { Capacitor } from '@capacitor/core';
import { knownPosition } from '@/lib/knownPosition';
import {
  applyFollowUp,
  applyInterpretation,
  applyToDraft,
  classifyAssistantError,
  conversationExpired,
  emptyDraft,
  nextFollowUp,
  planContextOf,
  quotaHint,
  settingsApplication,
  type AssistantApplication,
  type AssistantDraft,
  type FollowUp,
  type PendingPlace,
  type PlaceRole,
} from '@/lib/routeAssistant';
import { DEFAULT_PLANNER_SETTINGS, type PlannerPoint, type PlannerSettings } from '@/lib/plannerSettings';
import type { ActivityType } from '@/types';

/**
 * „Grout” asszisztens — a beszélgetés állapota az egész appban (#79).
 *
 * Három felület ír bele: a rögzítés képernyő gyors mezője, a Home „Mihez van ma
 * kedved?” mezője, és a chat. A tervező a rögzítés képernyő helyi hookjában él
 * (`useRoutePlanner`); amíg az a képernyő látszik, egy HÍDON (`PlannerBridge`)
 * át olvassuk és töltjük. Máshol egy piszkozatba (`draft`) gyűlik a terv, és
 * amikor a felhasználó a tervezőbe lép, egészben átkerül (`handoff`).
 *
 * ⚠️ CSAK ELŐTÖLT: tervezés sosem indul magától (Geri, #71).
 */

export type AssistantSurface = 'chat' | 'tracking' | 'home';
export type AssistantView = 'chat' | 'history' | 'memory';

export interface ChatEntry {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  status?: 'ok' | 'clarify' | 'not_route';
  applied?: string[];
  notes?: string[];
  pending?: PendingPlace[];
  /** A kérés kész, csak a mozgásforma hiányzik — gyorsválasz-gombok. */
  needsActivity?: boolean;
  /** Kész a terv: felkínáljuk a tervezőt. */
  ready?: boolean;
  remembered?: string[];
  /** Továbbkérdezés gyorsválaszokkal (#79). */
  followUp?: FollowUp;
  /** A gyorsválasz, amit választott („Mindegy” is). */
  answered?: string;
  /** A kérdéssor zárómondata. */
  closing?: boolean;
  /** #87: a modell gyorsválaszai — koppintásra üzenetként mennek el. */
  quickReplies?: string[];
}

export interface PlannerFieldsView {
  activityType: ActivityType | null;
  settings: PlannerSettings;
  from: PlannerPoint | null;
  to: PlannerPoint | null;
  stops: (PlannerPoint | null)[];
}

/** A rögzítés képernyő tervezője, ahogy az asszisztens látja. */
export interface PlannerBridge {
  fields: () => PlannerFieldsView;
  near: () => { lat: number; lng: number } | null;
  apply: (app: AssistantApplication) => void;
  setPoint: (role: PlaceRole, point: PlannerPoint) => void;
  setActivity: (type: ActivityType) => void;
  /** A beállító lap megnyitása (a mozgásforma-választóval együtt). */
  openPlanner: () => void;
}

export interface AssistantSummary {
  applied: string[];
  notes: string[];
  pending: PendingPlace[];
}

interface AssistantState {
  open: boolean;
  view: AssistantView;
  conversationId: string | null;
  lastAt: number;
  entries: ChatEntry[];
  /** Melyik beszélgetés üzenetei vannak betöltve (újraindítás után a szerverről jönnek). */
  loadedId: string | null;
  busy: boolean;
  error: string | null;
  quota: string | null;
  consentPending: { text: string; surface: AssistantSurface } | null;
  draft: AssistantDraft;
  /** A piszkozat átkerül a tervezőbe, amint a rögzítés képernyő átveszi. */
  handoff: boolean;
  bridge: PlannerBridge | null;
  lastNear: { lat: number; lng: number } | null;
  /** A legutóbbi kész kérés csipjei — a tervezőlap teteje mutatja. */
  summary: AssistantSummary | null;
  /** A beszélgetésben már eldőlt beállítások — ezekre nem kérdezünk rá (#79). */
  decided: string[];
  /**
   * A helyben, modellhívás nélkül lezajlott fordulók (továbbkérdezés, választás)
   * az utolsó szerverüzenet óta — a következő üzenettel felmennek (#80).
   */
  unsynced: LocalTurn[];
  /**
   * #87: a szerver (a modell) viszi a beszélgetést — a fix kérdéssor nem fut,
   * és a lenti választás is a modellhez megy. A #87 előtti szerver mellett
   * `false`, és minden a régi úton megy.
   */
  modelLed: boolean;

  openChat: (view?: AssistantView) => void;
  closeChat: () => void;
  setView: (view: AssistantView) => void;
  /** `choice`: a lenti gombokon választott (már beállított) érték — a szerver nem értelmezi újra (#87). */
  send: (text: string, surface: AssistantSurface, options?: { choice?: boolean }) => Promise<boolean>;
  acceptConsent: () => Promise<void>;
  declineConsent: () => void;
  newConversation: () => void;
  openConversation: (id: string) => Promise<void>;
  ensureLoaded: () => Promise<void>;
  choosePlace: (place: PendingPlace, point: PlannerPoint) => void;
  /** „Egyik sem” — a találat nem az, amire gondolt. */
  rejectPlace: (place: PendingPlace) => void;
  chooseActivity: (entryId: string, type: ActivityType) => void;
  /** `'navigate'`: a hívó vigye a rögzítés képernyőre (ott veszi át a piszkozatot). */
  goToPlanner: () => 'bridge' | 'navigate';
  registerBridge: (bridge: PlannerBridge) => void;
  unregisterBridge: (bridge: PlannerBridge) => void;
  consumeHandoff: () => AssistantDraft | null;
  /** Helyi állapot törlése a szerveroldali törlés után. */
  resetLocal: () => void;
  /** Gyorsválasz a továbbkérdezésre; `null`: „Mindegy”. */
  answerFollowUp: (entryId: string, value: string | null) => void;
  /**
   * A bejelentkezett felhasználóhoz köt. ⚠️ Más fióknál (vagy kijelentkezéskor)
   * a helyi beszélgetés és a piszkozat törlődik — egy közös készüléken se
   * lássa a következő felhasználó az előző üzeneteit.
   */
  bindUser: (uid: string | null) => void;
}

export interface LocalTurn {
  role: 'user' | 'assistant';
  text: string;
}

const ACTIVITY_WORD: Record<ActivityType, string> = { get run() { return getT().assistantStore.run; }, get walk() { return getT().assistantStore.walk; }, get ride() { return getT().assistantStore.ride; } };

const STORAGE_KEY = 'grundo.assistant.v1';

interface Persisted {
  uid: string | null;
  conversationId: string | null;
  lastAt: number;
}

function readPersisted(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { uid: null, conversationId: null, lastAt: 0 };
    const parsed = JSON.parse(raw) as { uid?: unknown; conversationId?: unknown; lastAt?: unknown };
    return {
      uid: typeof parsed.uid === 'string' ? parsed.uid : null,
      conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : null,
      lastAt: typeof parsed.lastAt === 'number' ? parsed.lastAt : 0,
    };
  } catch {
    return { uid: null, conversationId: null, lastAt: 0 };
  }
}

/** Kinek a beszélgetése van a tárban — fiókváltáskor nem vihető át. */
let boundUid: string | null = null;

function persist(conversationId: string | null, lastAt: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ uid: boundUid, conversationId, lastAt }));
  } catch {
    /* Privát ablak / tiltott tár: a beszélgetés a szerveren úgyis megvan. */
  }
}

/** The planner's chosen points as coordinates — the park on the way is searched between them (#86). */
function pointsOf(fields: { from: unknown; to: unknown }): { from?: { lat: number; lng: number }; to?: { lat: number; lng: number } } {
  const point = (value: unknown) => {
    const { lat, lng } = (value ?? {}) as { lat?: unknown; lng?: unknown };
    return typeof lat === 'number' && typeof lng === 'number' ? { lat, lng } : undefined;
  };
  return { from: point(fields.from), to: point(fields.to) };
}

/** A recent device fix, or `null` (no permission, no fix in time) — never blocks the message long. */
async function devicePosition(): Promise<{ lat: number; lng: number } | null> {
  try {
    /* Weben a telefon fiókba megosztott helye nyer (#86 — a böngésző becslése km-eket téved). */
    return await knownPosition({ timeoutMs: 4_000, maxAgeMs: 300_000 });
  } catch {
    return null;
  }
}

/**
 * „Parkon át” (#86): a park between the planner's start and destination goes in
 * as the first stop. The planner graph has no parks — the quiet route alone
 * still went along Tétényi út (Geri's screenshot).
 */
async function addGreenStop(get: () => AssistantState, set: StoreSet): Promise<void> {
  const state = get();
  const fields = state.bridge ? state.bridge.fields() : state.draft;
  const points = pointsOf(fields);
  const from = points.from ?? state.bridge?.near() ?? state.lastNear ?? undefined;
  const to = points.to ?? from;
  let text = getT().routeAssistant.greenNone;
  if (from && to) {
    try {
      const { stop } = await api.assistantGreenStop(from, to);
      if (stop) {
        const point: PlannerPoint = { lat: stop.lat, lng: stop.lng, label: stop.label };
        if (state.bridge) state.bridge.setPoint(0, point);
        else set((current) => ({ draft: withPoint(current.draft, 0, point) }));
        text = getT().routeAssistant.greenStop(stop.label);
      }
    } catch {
      /* Network or server error: quiet streets are already set — say so. */
    }
  }
  set((current) => ({
    unsynced: [...current.unsynced, { role: 'assistant', text }],
    entries: [...current.entries, { id: nextId(), role: 'assistant', text, status: 'ok', ready: true }],
  }));
}

let counter = 0;
const nextId = () => `e${Date.now().toString(36)}${(counter += 1)}`;

type StoreSet = (partial: Partial<AssistantState> | ((state: AssistantState) => Partial<AssistantState>)) => void;

/** Az utolsó Geri-üzenet kész-e (a „Megnyitom a tervezőben” ehhez igazodik). */
function lastEntryReady(entries: ChatEntry[]): boolean {
  return [...entries].reverse().find((entry) => entry.role === 'assistant')?.ready === true;
}

/**
 * A következő továbbkérdezés (#79) — vagy, ha minden megvolt, a zárómondat.
 * A kérdés is „kész” üzenet: a tervező közben végig elérhető, a kérdés csak
 * felajánlás.
 */
const closingText = () => getT().assistantStore.closing;

function askNext(get: () => AssistantState, set: StoreSet): void {
  const state = get();
  const fields = state.bridge ? state.bridge.fields() : state.draft;
  const followUp = nextFollowUp(new Set(state.decided), {
    activityType: fields.activityType,
    mode: fields.settings.mode,
  });
  const askedBefore = state.entries.some((entry) => entry.followUp);
  if (!followUp && !askedBefore) return;
  /*
    A zárómondat beszélgetésenként EGYSZER (Geri, #87): korábban minden kész
    válasz után újra jött („kerüljük a főutakat” → megint „Minden megvan…”).
  */
  if (!followUp && state.entries.some((entry) => entry.closing)) return;
  const text = followUp ? followUp.question : closingText();
  set((current) => ({
    unsynced: [...current.unsynced, { role: 'assistant', text }],
    entries: [
      ...current.entries,
      followUp
        ? { id: nextId(), role: 'assistant', text: followUp.question, status: 'ok', followUp, ready: true }
        : {
            id: nextId(),
            role: 'assistant',
            text,
            status: 'ok',
            ready: true,
            closing: true,
          },
    ],
  }));
}

function roleWord(role: PlaceRole): string {
  if (role === 'from') return getT().assistantStore.start;
  if (role === 'to') return getT().assistantStore.end;
  return getT().assistantStore.stop(role + 1);
}

/**
 * A szerephez tartozó választó lezárása minden üzenetben; jóváhagyásnál a
 * csip is megjelenik. Ha az üzenetnél már semmi nem vár, és kész kérés volt,
 * felkínálja a tervezőt.
 */
function resolvePending(entries: ChatEntry[], role: PlaceRole, chip: string | null): ChatEntry[] {
  return entries.map((entry) => {
    if (!entry.pending?.some((other) => other.role === role)) return entry;
    const pending = entry.pending.filter((other) => other.role !== role);
    return {
      ...entry,
      pending,
      applied: chip ? [...(entry.applied ?? []), chip] : entry.applied,
      /* Elutasított hely után a cél hiányzik — a tervező még nem ajánlható. */
      ready: chip !== null && entry.status === 'ok' && !entry.needsActivity && pending.length === 0,
    };
  });
}

function withPoint(draft: AssistantDraft, role: PlaceRole, point: PlannerPoint): AssistantDraft {
  if (role === 'from') return { ...draft, from: point };
  if (role === 'to') return { ...draft, to: point };
  const stops = [...draft.stops];
  stops[role] = point;
  return { ...draft, stops };
}

export const useAssistantStore = create<AssistantState>((set, get) => ({
  open: false,
  view: 'chat',
  /* A tárolt beszélgetés csak a `bindUser` után töltődik be — addig nem tudjuk, kié. */
  conversationId: null,
  lastAt: 0,
  entries: [],
  loadedId: null,
  busy: false,
  error: null,
  quota: null,
  consentPending: null,
  draft: emptyDraft(DEFAULT_PLANNER_SETTINGS),
  handoff: false,
  bridge: null,
  lastNear: null,
  summary: null,
  decided: [],
  unsynced: [],
  modelLed: false,

  openChat: (view = 'chat') => {
    set({ open: true, view, error: null });
    void get().ensureLoaded();
  },
  closeChat: () => set({ open: false }),
  setView: (view) => set({ view }),

  send: async (raw, surface, options) => {
    const text = raw.replace(/\s+/g, ' ').trim();
    if (!text || get().busy) return false;
    const now = Date.now();
    if (surface !== 'chat' && conversationExpired(get().lastAt, now)) get().newConversation();

    const userEntry: ChatEntry = { id: nextId(), role: 'user', text };
    set((state) => ({
      entries: [...state.entries, userEntry],
      busy: true,
      error: null,
      consentPending: null,
      /* A Home-ról és a chatből a beszélgetés azonnal látszik; a gyors mező csak kérdésnél nyitja. */
      ...(surface === 'tracking' ? {} : { open: true, view: 'chat' as const }),
    }));

    const bridge = get().bridge;
    const base = bridge ? bridge.fields() : get().draft;
    /*
      #86: „a Duna felé”, „egy park a közelben” is searched around the user —
      from Home there is no map position, so ask the device once (a cached fix
      is enough). It goes to our server for the map search, never to the model.
    */
    /* Weben nem a (régi, esetleg böngészős) gyorsítótárat — mindig a telefon friss helyét. */
    const near = bridge?.near()
      ?? (Capacitor.isNativePlatform() ? get().lastNear : null)
      ?? await devicePosition();
    if (near) set({ lastNear: near });
    const localTurns = get().unsynced;

    try {
      const result = await api.assistantMessage({
        conversationId: get().conversationId,
        text,
        near: near ?? undefined,
        plan: planContextOf(base),
        ...(localTurns.length ? { localTurns } : {}),
        points: pointsOf(base),
        ...(options?.choice ? { choice: true as const } : {}),
        modelReplies: true,
      });
      const { status } = result.interpretation;
      const app = status === 'not_route'
        ? null
        : applyInterpretation(result, { activityType: base.activityType, settings: base.settings });
      /* Kérdésnél is előtöltünk: ami elhangzott, az ne vesszen el a válaszig. */
      const liveBridge = get().bridge;
      if (app) {
        if (liveBridge) liveBridge.apply(app);
        else set((state) => ({ draft: applyToDraft(state.draft, app) }));
      }
      const activityAfter = app?.activityType ?? base.activityType;
      const needsActivity = status === 'ok' && !activityAfter;
      const entry: ChatEntry = {
        id: nextId(),
        role: 'assistant',
        text: result.reply,
        status,
        applied: app?.applied ?? [],
        notes: app?.notes ?? [],
        pending: app?.pending ?? [],
        needsActivity,
        /* Jóváhagyásra váró hely mellett még nem kész — előbb a „Erre gondoltál?” (#79). */
        ready: status === 'ok' && !needsActivity && (app?.pending.length ?? 0) === 0,
        remembered: result.remembered,
        quickReplies: result.quickReplies,
      };
      const modelLed = result.quickReplies !== undefined;
      set((state) => ({
        entries: [...state.entries, entry],
        busy: false,
        conversationId: result.conversationId,
        loadedId: result.conversationId,
        lastAt: now,
        quota: quotaHint(result.quotaLeft),
        summary: status === 'ok' && app
          ? { applied: app.applied, notes: app.notes, pending: app.pending }
          : state.summary,
        /* Amit kimondott, arra nem kérdezünk rá újra (a tervezővel egyezőre sem). */
        decided: [...new Set([...state.decided, ...(result.stated ?? [])])],
        /* A felküldöttek a szerveren vannak; ami közben keletkezett, az a következővel megy. */
        unsynced: state.unsynced.slice(localTurns.length),
        modelLed,
      }));
      persist(result.conversationId, now);

      /*
        A GYORS MEZŐ: ha a kérés egyből tervvé fordítható, a tervező nyílik;
        ha nem (kérdés, nem útvonal, hiányzó mozgásforma), a chat — Geri, #79.
      */
      if (surface === 'tracking') {
        if (entry.ready && liveBridge) {
          set({ open: false });
          liveBridge.openPlanner();
          return true;
        }
        set({ open: true, view: 'chat' });
      }
      /* A chatben a kész cél után továbbkérdezünk (Geri, #79) — a #87-től a modell kérdez. */
      if (entry.ready && !modelLed) askNext(get, set);
      return true;
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      set((state) => ({
        entries: state.entries.filter((entry) => entry.id !== userEntry.id),
        busy: false,
        ...(failure.kind === 'consent'
          ? { consentPending: { text, surface }, open: true, view: 'chat' as const }
          : { error: failure.text }),
      }));
      return failure.kind === 'consent';
    }
  },

  acceptConsent: async () => {
    const pending = get().consentPending;
    if (!pending) return;
    set({ busy: true, error: null });
    try {
      await api.assistantConsent();
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      set({ busy: false, error: failure.kind === 'message' ? failure.text : null });
      return;
    }
    set({ busy: false, consentPending: null });
    await get().send(pending.text, pending.surface);
  },

  declineConsent: () => set({ consentPending: null }),

  newConversation: () => {
    persist(null, 0);
    set((state) => ({
      conversationId: null,
      loadedId: null,
      lastAt: 0,
      entries: [],
      summary: null,
      decided: [],
      unsynced: [],
      modelLed: false,
      error: null,
      /* A tervező (ha látszik) a felhasználóé; a piszkozat viszont az előző beszélgetésé volt. */
      draft: emptyDraft(state.draft.settings),
      view: 'chat',
    }));
  },

  openConversation: async (id) => {
    set({ busy: true, error: null });
    try {
      const conversation = await api.assistantConversation(id);
      const lastAt = conversation.messages.at(-1)?.at ?? Date.now();
      set({
        conversationId: id,
        loadedId: id,
        lastAt,
        entries: conversation.messages.map((message) => ({
          id: nextId(),
          role: message.role,
          text: message.text,
          status: message.status,
        })),
        summary: null,
        decided: [],
        unsynced: [],
        busy: false,
        view: 'chat',
      });
      persist(id, lastAt);
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      set({ busy: false, error: failure.kind === 'message' ? failure.text : null });
    }
  },

  ensureLoaded: async () => {
    const { conversationId, loadedId, entries, busy } = get();
    if (!conversationId || loadedId === conversationId || entries.length > 0 || busy) return;
    try {
      const conversation = await api.assistantConversation(conversationId);
      if (get().conversationId !== conversationId || get().entries.length > 0) return;
      set({
        loadedId: conversationId,
        entries: conversation.messages.map((message) => ({
          id: nextId(),
          role: message.role,
          text: message.text,
          status: message.status,
        })),
      });
    } catch {
      /* Törölt vagy elérhetetlen beszélgetés: tiszta lappal folytatjuk. */
      get().newConversation();
    }
  },

  choosePlace: (place, point) => {
    const bridge = get().bridge;
    if (bridge) bridge.setPoint(place.role, point);
    else set((state) => ({ draft: withPoint(state.draft, place.role, point) }));
    const label = point.label ?? place.name;
    const chip = `${roleWord(place.role)}: ${label}`;
    /* #87: a választás a modellhez megy üzenetként — ott a beszélgetés a kontextusban folytatódik. */
    const toModel = get().modelLed && get().open;
    set((state) => ({
      unsynced: toModel ? state.unsynced : [...state.unsynced, { role: 'user', text: chip }],
      entries: resolvePending(state.entries, place.role, chip),
      summary: state.summary
        ? {
            ...state.summary,
            pending: state.summary.pending.filter((other) => other.role !== place.role),
            applied: [...state.summary.applied, `${roleWord(place.role)}: ${label}`],
          }
        : null,
    }));
    /* A memória ezt a JÓVÁHAGYOTT pontot köti a névhez — a következő „Keleti” már ez lesz. */
    void api
      .assistantRememberPlace({ name: place.name, label, lat: point.lat, lng: point.lng })
      .catch(() => undefined);
    if (toModel) {
      void get().send(chip, 'chat', { choice: true });
      return;
    }
    /*
      ⚠️ ITT NEM ÁLLHAT MEG A BESZÉLGETÉS (Geri, #79): a hely jóváhagyása helyben
      történik, modellhívás nélkül — ha kész lett a terv, a kérdéssort mi visszük
      tovább. Csak nyitott chatnél: a tervezőlap sávjából választva nem.
    */
    if (get().open && lastEntryReady(get().entries)) askNext(get, set);
  },

  rejectPlace: (place) => {
    const text = getT().assistantStore.reject(roleWord(place.role));
    set((state) => ({
      unsynced: [
        ...state.unsynced,
        { role: 'user', text: getT().assistantStore.none(roleWord(place.role), place.name) },
        { role: 'assistant', text },
      ],
      entries: [
        ...resolvePending(state.entries, place.role, null),
        { id: nextId(), role: 'assistant', text, status: 'clarify' },
      ],
      summary: state.summary
        ? { ...state.summary, pending: state.summary.pending.filter((other) => other.role !== place.role) }
        : null,
    }));
  },

  chooseActivity: (entryId, type) => {
    const bridge = get().bridge;
    if (bridge) bridge.setActivity(type);
    else set((state) => ({ draft: { ...state.draft, activityType: type } }));
    const toModel = get().modelLed && get().open;
    set((state) => ({
      unsynced: toModel ? state.unsynced : [...state.unsynced, { role: 'user', text: ACTIVITY_WORD[type] }],
      entries: state.entries.map((entry) =>
        entry.id === entryId
          ? { ...entry, needsActivity: false, ready: entry.status === 'ok' && !entry.pending?.length }
          : entry),
      decided: [...new Set([...state.decided, 'activity'])],
    }));
    if (toModel) {
      void get().send(ACTIVITY_WORD[type], 'chat', { choice: true });
      return;
    }
    if (lastEntryReady(get().entries)) askNext(get, set);
  },

  answerFollowUp: (entryId, value) => {
    const entry = get().entries.find((item) => item.id === entryId);
    const followUp = entry?.followUp;
    if (!entry || !followUp || entry.answered !== undefined) return;
    const bridge = get().bridge;
    const settings = bridge ? bridge.fields().settings : get().draft.settings;
    let answered = 'Mindegy';
    if (value !== null) {
      const result = applyFollowUp(settings, followUp.field, value);
      answered = result.chip;
      if (bridge) bridge.apply(settingsApplication(result.settings));
      else set((state) => ({ draft: { ...state.draft, settings: result.settings } }));
    }
    set((state) => ({
      unsynced: [...state.unsynced, { role: 'user', text: answered }],
      entries: state.entries.map((item) => (item.id === entryId ? { ...item, answered } : item)),
      decided: [...new Set([...state.decided, followUp.field])],
      summary: state.summary && value !== null
        ? { ...state.summary, applied: [...state.summary.applied, answered] }
        : state.summary,
    }));
    if (followUp.field === 'viaGreen' && value === 'park') {
      void addGreenStop(get, set).finally(() => askNext(get, set));
      return;
    }
    askNext(get, set);
  },

  goToPlanner: () => {
    const bridge = get().bridge;
    set({ open: false });
    if (bridge) {
      bridge.openPlanner();
      return 'bridge';
    }
    set({ handoff: true });
    return 'navigate';
  },

  registerBridge: (bridge) => set({ bridge }),

  unregisterBridge: (bridge) => {
    if (get().bridge !== bridge) return;
    /* A képernyő elhagyásakor a tervező állása lesz a piszkozat — a beszélgetés innen folytatódik. */
    const fields = bridge.fields();
    set({ bridge: null, draft: { ...fields } });
  },

  consumeHandoff: () => {
    if (!get().handoff) return null;
    const { draft } = get();
    set({ handoff: false });
    return draft;
  },

  resetLocal: () => {
    persist(null, 0);
    set((state) => ({
      conversationId: null,
      loadedId: null,
      lastAt: 0,
      entries: [],
      summary: null,
      decided: [],
      unsynced: [],
      modelLed: false,
      consentPending: null,
      error: null,
      quota: null,
      draft: emptyDraft(state.draft.settings),
    }));
  },

  bindUser: (uid) => {
    if (boundUid === uid) return;
    const stored = readPersisted();
    boundUid = uid;
    if (uid && stored.uid === uid) {
      set({ conversationId: stored.conversationId, lastAt: stored.lastAt });
      return;
    }
    set({ open: false, handoff: false, lastNear: null });
    get().resetLocal();
  },
}));
